/**
 * Recipient journey, Part 5: Stage 2, her own question. A little trust.
 *
 * Small pure pieces the room uses:
 *  - ownReadingFrom: her own reading started from the gift room carries the
 *    package's invite (R5.2), so the server opens it from the topic;
 *  - the two lines the Mystic says after it ("What landed?", then "I've kept
 *    these for you." with "these" as a link to the Journal sheet, R5.4, R5.6);
 *  - the Journal sheet's two readings: the package (her card inside it) and
 *    her own (R5.7);
 *  - "Keep this" (R5.9, R5.10): the words, and a "Did you mean gmail.com?"
 *    suggestion for a common misspelt domain.
 */

export const WHAT_LANDED = 'What landed?'
export const KEPT_LINE = "I've kept these for you."
export const KEPT_LINK = 'these'
export const KEEP_LINK = 'Keep this'
export const KEEP_ASK = 'Your email, and we send you one link to keep it.'
export const KEEP_SENT = "Sent. Tap the link in that email and it's yours on any device."

/** What her own reading needs to remember of the room it started from. */
export function ownReadingFrom (room) {
  if (!room || !room.inv) return null
  const gift = room.gift || {}
  return { inv: String(room.inv), short: room.short || '', from_name: gift.from_name || '', keep: !!gift.keep }
}

/**
 * Where a reading started from the gift room goes when she leaves it (the
 * reading's ✕, Finish, "Exit Reading"): back to the room it came from, never
 * the Mystic's empty doorstep (gift bug walk 2026-10-25 #7, #9). '' when the
 * reading did not come from a room.
 */
export function roomToReturnTo (own) {
  if (!own || !own.inv || !own.short) return ''
  return `/?reading=${encodeURIComponent(own.short)}&inv=${encodeURIComponent(own.inv)}`
}

/**
 * e2e S21 (2026-10-30): the way back from their own reading to the room is a
 * full reload. Marked in this tab only (sessionStorage), by the room's invite,
 * so the reopened room knows their own reading is done and the reveal it
 * earned (the First Star) plays here, not on the next app open.
 */
const OWN_BACK_KEY = 'own_reading_back'
export function markOwnReadingBack (inv) {
  if (!inv) return
  try { window.sessionStorage.setItem(OWN_BACK_KEY, String(inv)) } catch (e) { /* private mode: the next open plays it */ }
}
export function ownReadingCameBack (inv) {
  if (!inv) return false
  try { return window.sessionStorage.getItem(OWN_BACK_KEY) === String(inv) } catch (e) { return false }
}

/**
 * Whether the Quest's reveals wait because a reading is under way. A reveal
 * plays where it is earned, the gift room included (Chris's go 2026-10-27;
 * recipient polish N12, 2026-11-02): the room on screen is never "mid-reading"
 * itself, though its state is the gift's reading. A card being drawn, a
 * rating or a "What landed?" still holds it, in the room too (`drawing`).
 * An ordinary reading holds it until its conclusion.
 */
export function midReadingForReveals ({ inSession = false, state = '', giftRoomShown = false, drawing = false } = {}) {
  if (drawing) return true
  if (giftRoomShown) return false
  return !!inSession && !['READING_CONCLUSION', 'RECEPTION'].includes(state)
}

/**
 * The room once they have written back (walk 2026-11-02): thanked, and no draw
 * in progress any more. The draw's `deeperActive` could outlive the card (the
 * reopen keeps it once the server says drawn), and the Quest's reveal gate
 * reads it as a draw, so the First Star earned here waited for a reload.
 */
export function roomAfterThanks (room, reply = {}) {
  if (!room) return room
  return { ...room, deeperActive: false, room: { ...(room.room || {}), thanked: true, reply } }
}

/**
 * "I've kept these for you." after their own reading: the 30-second timer
 * never says it while "What landed?" waits for their answer (recipient polish
 * N16: it came first, above Chi's answer). Their answer brings it.
 */
export function keptLineDue ({ asking = false } = {}) {
  return !asking
}

/**
 * Their own reading's address (own-reading fix walk 2026-10-26): the room's
 * (?reading=<gift>&inv=, so "‹ Sam's gift" and a reload still know the room)
 * plus ?own=1 in the interview and ?own=<its short> once drawn. inv stays the
 * one redacted param; own is not a secret. '' when it is not their own reading
 * from a room (a reading for one of her people has its own, ?reading=<id>).
 */
export function ownReadingAddress (href, own, readingId = '') {
  if (!keepsRoomAddress(own)) return ''
  try {
    const url = new URL(href)
    url.searchParams.set('reading', own.short)
    url.searchParams.set('inv', own.inv)
    url.searchParams.set('own', readingId ? String(readingId) : '1')
    url.searchParams.delete('reader')
    return url.pathname + url.search
  } catch (e) { return '' }
}

/** A reload's ?own=: { interview } for the interview, { short } once drawn; null otherwise. */
export function ownToReopen (query) {
  const own = query && query.own ? String(query.own) : ''
  if (!own || !query.reading || !query.inv) return null
  return own === '1' ? { interview: true, short: '' } : { interview: false, short: own }
}

/** Their own reading from a room keeps the room's address through the draw. */
export function keepsRoomAddress (own) {
  return !!roomToReturnTo(own) && !own.forName
}

/** The closing line as segments: "these" is the link (R5.6). */
export function keptLineSegments (text = KEPT_LINE) {
  const at = text.indexOf(KEPT_LINK)
  if (at === -1) return [{ text }]
  return [
    ...(at ? [{ text: text.slice(0, at) }] : []),
    { text: KEPT_LINK, link: true },
    ...(at + KEPT_LINK.length < text.length ? [{ text: text.slice(at + KEPT_LINK.length) }] : []),
  ]
}

/**
 * The Journal during Stage 2 (R5.7): the packages she received, each with
 * her card inside it, and her own readings. A deeper card is the package's
 * child, never a row of its own.
 */
export function journalEntries (received = [], sessions = []) {
  return {
    packages: (Array.isArray(received) ? received : []).filter(Boolean),
    own: (Array.isArray(sessions) ? sessions : []).filter((s) => s && !s.is_deeper),
  }
}

// The domains people mean most often, and the ways they are mistyped.
const COMMON_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com',
  'live.com', 'msn.com', 'me.com', 'comcast.net', 'proton.me', 'protonmail.com']

function distance (a, b) {
  const m = a.length; const n = b.length
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)])
  for (let j = 1; j <= n; j++) d[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
    }
  }
  return d[m][n]
}

/**
 * "Did you mean maya@gmail.com?" for an address whose domain is one or two
 * letters from a common one ("gmial.com", "gmail.con", "hotmial.com").
 * '' when it looks right, or is not an address yet.
 */
export function suggestEmail (email = '') {
  const value = String(email || '').trim()
  const at = value.lastIndexOf('@')
  if (at < 1 || at === value.length - 1) return ''
  const local = value.slice(0, at)
  const domain = value.slice(at + 1).toLowerCase()
  if (COMMON_DOMAINS.includes(domain)) return ''
  let best = ''; let bestScore = 3
  for (const d of COMMON_DOMAINS) {
    const score = distance(domain, d)
    if (score < bestScore) { best = d; bestScore = score }
  }
  return best && bestScore <= 2 ? `${local}@${best}` : ''
}

/** Plain-looking email (the server checks properly). */
export function looksLikeEmail (email = '') {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim())
}

/** After the "Keep this" link is tapped: it is hers, on any device. */
export const KEPT_HOME_LINE = 'It\'s yours now. On any device, sign in with that address and tap "Email me a code instead" to come back here.'

/**
 * R5.1, in the room: her deeper card has been read when the deeper draw's
 * after-card buttons arrive (the set carries `deeper_fresh`). The room then
 * hands back to its own two chips at once ("Ask me something of your own"
 * and the thank, or the stars once thanked): the deeper draw closes and the
 * card is drawn. Returns the next gift room, or null when nothing changes.
 * The caller offers none of the after-card buttons in the gift room (never
 * more than two chips at once, spec principle 4); the Respond box stays.
 */
/**
 * The room after closeDeeperPicker's reopen of the package. The reopen now
 * waits for her pick to be answered (utils/pick-in-flight.js), and by then her
 * card is read and afterCardRead has closed the draw: keep that, and take the
 * server's room. An earlier draft forced deeperActive back on here, which hid
 * the Stage 2 chips. Never undoes a closed draw.
 */
export function roomAfterReopen (current, reopenedRoom) {
  if (!current) return current
  return { ...current, room: reopenedRoom || current.room }
}

export function afterCardRead (giftRoom, actions) {
  if (!giftRoom || !giftRoom.room || !Array.isArray(actions)) return null
  if (!actions.some((a) => a && a.action_type === 'deeper_fresh')) return null
  return { ...giftRoom, deeperActive: false, room: { ...giftRoom.room, deeper: 'drawn' } }
}

/**
 * Gift card order (2026-10-29): her card is read the moment its words land
 * (the live gift panel has its reading), not when the after-card buttons
 * come; those follow a model call for the child's title and suggestions that
 * the room never shows. The room closes the draw then, so the bridge and the
 * two chips follow the read at once. Returns the next gift room, or null
 * (not read yet, not a live gift card, or already drawn).
 */
export function giftCardRead (giftRoom, messages) {
  if (!giftRoom || !giftRoom.room || giftRoom.room.deeper === 'drawn') return null
  const ms = Array.isArray(messages) ? messages : []
  const read = ms.some((m) => m && m._context === 'talk-card' && m._live && m.talk_card
    && m.talk_card.kind === 'gift' && String(m.talk_card.reading || '').trim())
  return read ? { ...giftRoom, deeperActive: false, room: { ...giftRoom.room, deeper: 'drawn' } } : null
}
